import { getEmailData } from "../utilities/emailParser";
import { listDirectory, uploadFileRequest } from "./documentActions";
import { openGlobalDialog } from "./globalEditDialogActions";
import API from "../utilities/LocalApiProxy";
import { mapFirmDocsMatterID } from "../utilities/utilities";
import {
	MULTIPLE_FILE_UPLOAD_SUCCESS,
	EMAIL_DUPLICATES_FOUND_SUCCESS,
	MULTIPLE_FILE_UPLOAD_FAILURE,
	EMAIL_NOTE_UPDATED,
	OPEN_REACT_DIALOG,
	UPDATE_REACT_DIALOG_CONTENT,
	CLOSE_REACT_DIALOG,
	OPEN_PERSISTENT_SNACKBAR,
	OPEN_GLOBAL_SNACKBAR,
} from "./types";
import { createFilesUploadingContentForDialog } from "../components/reactDialog";
import { isPreventActionOnDeletedResource } from "./errorActions";

async function createEmailData(files, progressCallback) {
	let { emailsToUpload, failedToParseEmails } = await getEmailData(
		files,
		progressCallback
	);
	return { emailsToUpload, failedToParseEmails };
}

export function uploadMultipleEmails(
	matterID,
	basepath,
	directoryID,
	files,
	skipMD5Check,
	callback
) {
	return function (dispatch) {
		const emailParseProgressFn = ({ counter, total, filename }) => {
			dispatch({
				type: UPDATE_REACT_DIALOG_CONTENT,
				payload: {
					showSpinnerInTitle: true,
					mainContent: createFilesUploadingContentForDialog(
						[filename],
						`Preparing ${counter} of ${total} Emails:`
					),
					counter: counter,
				},
			});
		};

		dispatch({
			type: OPEN_REACT_DIALOG,
			payload: {
				showSpinnerInTitle: true,
				title: "Uploading Emails",
				mainContent: `Preparing to upload emails`,
			},
		});

		createEmailData(files, emailParseProgressFn)
			.then(({ emailsToUpload, failedToParseEmails }) => {
				if (
					failedToParseEmails &&
					failedToParseEmails.length > 0 &&
					emailsToUpload.length === 0
				) {
					console.error(
						"Some emails failed to parse and will not be uploaded:",
						failedToParseEmails
					);
					// open a warning snackbar with the names of the files that failed
					const failedNames = failedToParseEmails
						.map((f) => f.name)
						.join(", ");
					const numFailed = failedToParseEmails.length;
					dispatch({
						type: OPEN_PERSISTENT_SNACKBAR,
						payload: {
							variant: "error",
							message: `We were unable to parse ${numFailed} email${
								numFailed > 1 ? "s" : ""
							} (${failedNames}) and they were not uploaded.`,
						},
					});
					dispatch({
						type: CLOSE_REACT_DIALOG,
					});
				} else if (emailsToUpload.length === 0) {
					dispatch({ type: MULTIPLE_FILE_UPLOAD_FAILURE });
					dispatch({
						type: CLOSE_REACT_DIALOG,
					});
				} else {
					insertEmailRecords(
						directoryID,
						emailsToUpload,
						skipMD5Check
					)
						.then((response) => {
							var id;
							const start = response.mysql.insertId;
							var now = new Date();
							for (
								var i = 0;
								i < response.mysql.affectedRows;
								i++
							) {
								id = start + i;
								emailsToUpload[i].id = id;
								emailsToUpload[i].uuid =
									id +
									"_" +
									emailsToUpload[i].originalFilename;

								emailsToUpload[i].metadata = {
									//at the time this was developed, emails can only be added to matters so this is not strictly necessary.  If emails can ever be added to firm docs then... this should still work!
									matterID:
										"" +
										mapFirmDocsMatterID(matterID, basepath),
									emailDate:
										"" +
										emailsToUpload[i].dateObj.getTime(),
									emailFrom: encodeURIComponent(
										emailsToUpload[i].emailFrom
									),
									emailTo: encodeURIComponent(
										emailsToUpload[i].emailTo
									),
									emailSubject: encodeURIComponent(
										emailsToUpload[i].subjectline
									),
									emailAddedDate: "" + now.getTime(),
									uploadSource: "web",
								};
							}
							asyncUploadMultipleEmails(
								basepath,
								files,
								emailsToUpload,
								dispatch
							)
								.then(() => {
									listDirectory(matterID, basepath)(dispatch);
									if (failedToParseEmails.length > 0) {
										const failedNames = failedToParseEmails
											.map((f) => f.name)
											.join(", ");
										const numFailed =
											failedToParseEmails.length;
										dispatch({
											type: OPEN_PERSISTENT_SNACKBAR,
											payload: {
												variant: "warning",
												message: `We were unable to parse ${numFailed} email${
													numFailed > 1 ? "s" : ""
												} (${failedNames}) and they were not uploaded.`,
												snackbarKey:
													"emailUploadParseWarning",
											},
										});
									}

									dispatch({
										type: MULTIPLE_FILE_UPLOAD_SUCCESS,
										payload: {
											num: emailsToUpload.length,
											objectType: "Email",
										},
									});
									dispatch({
										type: CLOSE_REACT_DIALOG,
									});
									callback();
								})
								.catch((err) => {
									console.error(
										"problem in multiple upload",
										err
									);
								});
						})
						.catch(({ lxwErrorCode, lxwErrorData }) => {
							if (lxwErrorCode === 6510) {
								dispatch({
									type: EMAIL_DUPLICATES_FOUND_SUCCESS,
								});
								dispatch({
									type: CLOSE_REACT_DIALOG,
								});
								const { num, duplicateChecksums = [] } =
									lxwErrorData;
								const duplicateChecksumSet = new Set(
									duplicateChecksums
								);
								const numNonDuplicates = emailsToUpload.filter(
									(email) =>
										!duplicateChecksumSet.has(
											email.md5checksum
										)
								).length;
								const total = emailsToUpload.length;
								openGlobalDialog(
									{
										label: `${num} Duplicate Email${
											num > 1 ? "s" : ""
										} Found`,
										mainText:
											total > 1
												? `${num} of the ${total} selected emails ${
														num > 1 ? "have" : "has"
													} already been uploaded to this matter.`
												: "This email has already been uploaded to this matter.",
										errorMessage: "",
										canSubmit: true,
										hideTextfield: true,
										readOnly: true,
										hideCancelButton: true,
										type: "emailDuplicatesFound",
										value: {
											numNonDuplicates,
											numDuplicates: num,
											total,
										},
									},
									(dialogState) => {
										if (
											dialogState &&
											dialogState.uploadNonDuplicatesOnly
										) {
											const nonDuplicateFiles =
												Array.from(files).filter(
													(file, i) =>
														emailsToUpload[i] &&
														!duplicateChecksumSet.has(
															emailsToUpload[i]
																.md5checksum
														)
												);
											uploadMultipleEmails(
												matterID,
												basepath,
												directoryID,
												nonDuplicateFiles,
												true, // these are already confirmed non-duplicates, no need to re-check
												callback
											)(dispatch);
										} else {
											uploadMultipleEmails(
												matterID,
												basepath,
												directoryID,
												files,
												true, //user chose to upload all including duplicates, so skipMD5Check=true
												callback
											)(dispatch);
										}
									},
									() => ({ canSubmit: true })
								)(dispatch);
							} else if (
								isPreventActionOnDeletedResource(lxwErrorCode)
							) {
								// need to close the email upload dialog box
								// everything else is handled in errorActions
								dispatch({
									type: CLOSE_REACT_DIALOG,
								});
							}
						});
				}
			})
			.catch((err) => {
				console.error("problem parsing or uploading emails");
				console.error(err);
				dispatch({
					type: CLOSE_REACT_DIALOG,
				});
				dispatch({ type: MULTIPLE_FILE_UPLOAD_FAILURE });
			});
	};
}
function insertEmailRecords(directoryID, emailList, skipMD5Check) {
	return API.put("/email/checkTrashAndUpload", {
		body: { directoryID, emailList, skipMD5Check },
	});
}
function confirmEmailUpload(IDs) {
	return API.post("/email/confirmUpload", {
		body: { IDs },
	});
}
export function updateEmailNote({ emailID, value, showSuccessSnackbar } = {}) {
	return function (dispatch) {
		API.post("/email/note", { body: { emailID, value } })
			.then(() => {
				dispatch({
					type: EMAIL_NOTE_UPDATED,
					payload: { id: emailID, value },
				});
				if (showSuccessSnackbar) {
					dispatch({
						type: OPEN_GLOBAL_SNACKBAR,
						payload: {
							message: "Email Short Note Updated",
							variant: "success",
						},
					});
				}
			})
			.catch((err) => {
				console.error("Error updating email note", err);
			});
	};
}
const EMAIL_S3_UPLOAD_CHUNK_SIZE = 4;

async function asyncUploadMultipleEmails(basepath, files, fileList, dispatch) {
	const emailsWithFiles = fileList.map((item, i) => ({
		emailID: item.id,
		metadata: item.metadata,
		file: files[i],
		name: files[i].name,
	}));

	const chunks = chunkArray(emailsWithFiles, EMAIL_S3_UPLOAD_CHUNK_SIZE);
	const allUploadedIDs = [];

	for (let k = 0; k < chunks.length; k++) {
		const chunk = chunks[k];
		const fileCounter = k * EMAIL_S3_UPLOAD_CHUNK_SIZE;
		const maxFile = fileCounter + EMAIL_S3_UPLOAD_CHUNK_SIZE;

		dispatch({
			type: UPDATE_REACT_DIALOG_CONTENT,
			payload: {
				showSpinnerInTitle: true,
				title: "Uploading Emails",
				mainContent: createFilesUploadingContentForDialog(
					chunk.map((e) => e.name),
					`Uploading ${fileCounter + 1}-${
						maxFile > emailsWithFiles.length
							? emailsWithFiles.length
							: maxFile
					} of ${emailsWithFiles.length} Emails:`
				),
				counter: fileCounter,
			},
		});

		const uploadedIDs = await bulkGetSignedUrlsForEmails(chunk);
		if (uploadedIDs.length > 0) {
			await confirmEmailUpload(uploadedIDs);
		}
		allUploadedIDs.push(...uploadedIDs);
	}

	return allUploadedIDs;
}

function bulkGetSignedUrlsForEmails(chunk) {
	return API.post("/bulk/multipleSignedUrlForEmailUpload", {
		body: {
			emails: chunk.map((e) => ({
				emailID: e.emailID,
				metadata: e.metadata,
			})),
		},
	}).then((res) => {
		const signedUrls = res.mysql.urls;
		const fileMap = {};
		chunk.forEach((e) => {
			fileMap[e.emailID] = e.file;
		});

		const promises = signedUrls.map((r) =>
			uploadFileRequest(r.url, fileMap[r.emailID]).then(() => r.emailID)
		);

		return Promise.allSettled(promises).then((results) =>
			results.filter((r) => r.status === "fulfilled").map((r) => r.value)
		);
	});
}

function chunkArray(arr, len) {
	var chunks = [],
		i = 0,
		n = arr.length;
	while (i < n) {
		chunks.push(arr.slice(i, (i += len)));
	}
	return chunks;
}
export function getSignedUrlForEmailAndDownload(emailID) {
	return function (dispatch) {
		API.get("/signedUrlForEmail", { queryParams: { emailID } })
			.then((res) => {
				if (res.mysql.noS3Object) {
					dispatch({
						type: OPEN_GLOBAL_SNACKBAR,
						payload: {
							message:
								"This Email could not be downloaded.  Please contact LexWorkplace Support",
							variant: "error",
							loadingSpinnerVisible: false,
							allowClose: true,
							autoHideDuration: null,
						},
					});
				} else {
					window.location.href = res.mysql.url;
					dispatch({
						type: "GET_SIGNED_URL_SUCCESS",
					});
				}
			})
			.catch((err) => console.log(err));
	};
}
